import Vue from 'vue';
import VueRouter from 'vue-router';
import Plannings from '../views/Plannings';
import CapexPlanning from '../views/CapexPlanning';
import CapexPlanningNew from '../views/CapexPlanningNew';
import SalesPlanningNew from '../views/SalesPlanningNew';
import RiskPlanning from '../views/Risk';
import SummaryRISK from '../views/SummaryRISK';
import OtherInput from '../views/OtherInput';
import Charts from '../views/Charts';
import FundingInput from '../views/FundingInput';
import FundingInputNew from '../views/FundingInputNew';
import ExistingFunding from '../views/ExistingFunding';
import CapexOLPlanning from '../views/CapexOLPlanning';
import CapexOLPlanningNew from '../views/CapexOLPlanningNew';
import BalanceSheet from '../views/BalanceSheet';
import BalanceSheetNew from '../views/BalanceSheetNew';
import ProfitLoss from '../views/ProfitLoss';
import ProfitLossNew from '../views/ProfitLossNew';
import SummaryPL from '../views/SummaryPL';
import TaxInput from '../views/TaxInput';
import SummaryBS from '../views/SummaryBS';
import SummaryPlEco from '../views/SummaryPlEco';
import SummaryBsEco from '../views/SummaryBsEco';
import SummaryCAPEX from '../views/SummaryCAPEX';
import SummaryDetailPositions from '../views/SummaryDetailPositions';
import ExistingLoansPositions from '../views/ExistingLoansPositions';
import ExistingBusinessFullAnalytics from '../views/ExistingBusinessFullAnalytics';
import Assets from '../views/Assets';
import SalesDetailPositions from '../views/SalesDetailPositions';
import SummaryCAPEXold from '../views/SummaryCAPEXold';
import SummaryPEREX from '../views/SummaryPEREX';
import SummaryOPEX from '../views/SummaryOPEX';
import IncreaseAmount from '../views/IncreaseAmount';
import SummaryNewProduction from '../views/SummaryNewProduction';
import SummaryPortfolio from '../views/SummaryPortfolio';
import FxRates from '../views/ExchangeRates';
import Options from '../views/Options';
import Euribor from '../views/Euribor';
import OptionCategory from '../views/OptionCategory';
import ProductMapping from '../views/ProductMapping';
import KontoMapping from '../views/KontoMapping';
import OtherFinreps from '../views/OtherFinreps';
import ArchivePositions from '../views/ArchivePositions';
import PlanningComment from '../views/PlanningComment';
import Validator from '../views/Validator';
import Login from '../views/Login';
import UserManagement from '../views/User';
import Tickets from '../views/Tickets';
import UserActivityLogs from '../views/UserActivityLog';
import PerformanceMetrics from '../views/PerformanceMetrics';
import Admin from '../views/Admin';
import { isAuthenticated } from '../utilities/isAuthenticated';
import { clearAuthStorage } from '../utilities/authSession';

import SummaryBsEcoFull from '../views/SummaryBsEcoFull';
import SummaryPlEcoFull from '../views/SummaryPlEcoFull';

Vue.use(VueRouter);

// Shared guard: keeps the requested page as `redirect` so a deep link (for
// example a planning link from a ticket email) survives the login screen, and
// sends the user back to login when the link belongs to another entity.
function requireAuth(to, from, next) {
  if (!isAuthenticated()) {
    next({ path: '/login', query: { redirect: to.fullPath } });
    return;
  }

  const linkEntity = String(to.query.entity || '').toLowerCase();
  const sessionEntity = String(localStorage.getItem('entity_code') || '').toLowerCase();
  // 'le' is the ad-hoc local API, which may point at any entity database.
  const entityKnown = sessionEntity && sessionEntity !== 'none' && sessionEntity !== 'le';

  if (linkEntity && entityKnown && linkEntity !== sessionEntity) {
    // The session belongs to another entity's API, so it cannot serve this
    // link. Drop it, otherwise the login route would send the user straight
    // back here and the two guards would bounce the request between them.
    clearAuthStorage();
    next({ path: '/login', query: { redirect: to.fullPath } });
    return;
  }

  next();
}

const nestedSidebarItems = [
  {
    groupKey: 'sidebar_group_data_input',
    icon: 'mdi-database-edit',
    items: [
      {
        translationKey: 'sidebar_plannings_capex',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/capex_new',
      },
      {
        translationKey: 'sidebar_plannings_sales',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/salesnew',
      },
      {
        translationKey: 'sidebar_plannings_capex_ol',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/capex_ol_new',
      },
      {
        translationKey: 'Other Input',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/other',
      },
      {
        translationKey: 'plannings_funding',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/funding_new',
      },
      {
        translationKey: 'existing_funding',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/existing_funding',
      },
      {
        translationKey: 'sidebar_comments',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/comments',
      },
      {
        translationKey: 'sidebar_archive',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/archive_positions',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_financial_statements',
    icon: 'mdi-file-document-outline',
    items: [
      {
        translationKey: 'sidebar_plannings_bs',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/balance_sheet_new',
      },
      {
        translationKey: 'sidebar_plannings_pl',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/profit_loss_new',
      },
      {
        translationKey: 'Tax',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/tax_input',
      },
      {
        translationKey: 'sidebar_plannings_summary_pl',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_pl',
      },
      {
        translationKey: 'sidebar_plannings_summary_bs',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_bs',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_summary_reports',
    icon: 'mdi-chart-bar',
    items: [
      {
        translationKey: 'summary_risk',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_risk',
      },
      {
        translationKey: 'sidebar_plannings_summary_capex',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_capex',
      },
      {
        translationKey: 'OPEX',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_opex',
      },
      {
        translationKey: 'sidebar_plannings_summary_perex',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_perex',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_portfolio',
    icon: 'mdi-briefcase-outline',
    items: [
      {
        translationKey: 'plannings_new_production',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_new_production',
      },
      {
        translationKey: 'plannings_portfolio',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_portfolio',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_economic_view',
    icon: 'mdi-finance',
    items: [
      {
        translationKey: 'BS ECO',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_bs_eco_full',
      },
      {
        translationKey: 'PL ECO',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_pl_eco_full',
      },
      {
        translationKey: 'Summary BS ECO',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_bs_eco',
      },
      {
        translationKey: 'Summary PL ECO',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/summary_pl_eco',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_market_rates',
    icon: 'mdi-currency-usd',
    items: [
      {
        translationKey: 'sidebar_exchange_rates',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/fx_rates',
      },
      {
        translationKey: 'sidebar_euribor',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/euribor',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_analytical_reports',
    icon: 'mdi-magnify-expand',
    items: [
      {
        translationKey: 'sidebar_detail',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/detail_positions',
      },
      {
        translationKey: 'sidebar_existing_loans',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/existing_loans_positions',
      },
      {
        translationKey: 'existing_business_full_analytics',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/existing_business_full_analytics',
      },
      {
        translationKey: 'sidebar_assets',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/assets',
      },
      {
        translationKey: 'sidebar_sales_detail',
        routeSupplantKeys: ['planningId'],
        route: '/plannings/{planningId}/sales_detail_positions',
      },
    ],
  },
  {
    groupKey: 'sidebar_group_management_reports',
    icon: 'mdi-presentation',
    items: [
      // {
      //   translationKey: 'Charts',
      //   routeSupplantKeys: ['planningId'],
      //   route: '/plannings/{planningId}/charts',
      // },
    ],
    actions: ['generateReport', 'generateExcel'],
  },
];

const routes = [
  {
    path: '/plannings',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    // () => import(/* webpackChunkName: "about" */ ${component path})
    component: Plannings,
    name: 'plannings',
    beforeEnter: requireAuth,
  },
  {
    path: '/plannings/:planningId',
    component: CapexPlanning,
    name: 'planningCapex',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/capex_new',
    component: CapexPlanningNew,
    name: 'CapexPlanningNew',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/salesnew',
    component: SalesPlanningNew,
    name: 'planningSalesNew',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/capex_ol',
    component: CapexOLPlanning,
    name: 'capexOLPlanning',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/capex_ol_new',
    component: CapexOLPlanningNew,
    name: 'CapexOLPlanningNew',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/risk',
    component: RiskPlanning,
    name: 'riskPlanning',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_risk',
    component: SummaryRISK,
    name: 'summaryRisk',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/increase',
    component: IncreaseAmount,
    name: 'IncreaseAmount',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/funding',
    component: FundingInput,
    name: 'fundingInput',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/funding_new',
    component: FundingInputNew,
    name: 'fundingInputNew',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/existing_funding',
    component: ExistingFunding,
    name: 'existingFunding',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/other',
    component: OtherInput,
    name: 'otherInput',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/tax_input',
    component: TaxInput,
    name: 'taxInput',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/balance_sheet',
    component: BalanceSheet,
    name: 'BalanceSheet',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/euribor',
    component: Euribor,
    name: 'Euribor',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/balance_sheet_new',
    component: BalanceSheetNew,
    name: 'BalanceSheetNew',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/profit_loss_new',
    component: ProfitLossNew,
    name: 'ProfitLossNew',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/profit_loss',
    component: ProfitLoss,
    name: 'ProfitLoss',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_pl',
    component: SummaryPL,
    name: 'SummaryPL',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_bs',
    component: SummaryBS,
    name: 'SummaryBS',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_capex',
    component: SummaryCAPEX,
    name: 'SummaryCAPEX',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_capex_old',
    component: SummaryCAPEXold,
    name: 'SummaryCAPEXold',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_perex',
    component: SummaryPEREX,
    name: 'SummaryPEREX',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_opex',
    component: SummaryOPEX,
    name: 'SummaryOPEX',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_new_production',
    component: SummaryNewProduction,
    name: 'SummaryNewProduction',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_portfolio',
    component: SummaryPortfolio,
    name: 'SummaryPortfolio',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_pl_eco',
    component: SummaryPlEco,
    name: 'SummaryPlEco',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_bs_eco',
    component: SummaryBsEco,
    name: 'SummaryBsEco',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_bs_eco_full',
    component: SummaryBsEcoFull,
    name: 'SummaryBsEcoFull',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/summary_pl_eco_full',
    component: SummaryPlEcoFull,
    name: 'SummaryPlEcoFull',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/charts',
    component: Charts,
    name: 'Charts',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/fx_rates',
    component: FxRates,
    name: 'FxRates',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/comments',
    component: PlanningComment,
    name: 'PlanningComment',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/archive_positions',
    component: ArchivePositions,
    name: 'ArchivePosition',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/detail_positions',
    component: SummaryDetailPositions,
    name: 'SummaryDetailPosition',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/existing_business_full_analytics',
    component: ExistingBusinessFullAnalytics,
    name: 'ExistingBusinessFullAnalytics',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/existing_loans_positions',
    component: ExistingLoansPositions,
    name: 'ExistingLoansPositions',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/assets',
    component: Assets,
    name: 'Assets',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  {
    path: '/plannings/:planningId/sales_detail_positions',
    component: SalesDetailPositions,
    name: 'SalesDetailPosition',
    beforeEnter: requireAuth,
    meta: {
      sidebarNestedItems: {
        active: true,
        translationKey: 'sidebar_plannings',
        prependIcon: 'mdi-gavel',
        items: nestedSidebarItems,
      },
    },
  },
  { // TODO: Remove this when login screen is created
    path: '/',
    redirect: '/plannings',
  },
  {
    path: '/login',
    beforeEnter: (to, from, next) => {
      const authenticated = isAuthenticated();
      if (authenticated) {
        const redirectTarget = to.query.redirect && to.query.redirect !== '/login'
          ? to.query.redirect
          : '/';
        next(redirectTarget);
      } else next();
    },
    component: Login,
  },
  {
    path: '/options',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    // () => import(/* webpackChunkName: "about" */ ${component path})
    component: Options,
    name: 'options',
    beforeEnter: requireAuth,
  },
  {
    path: '/options/:optionId',
    component: OptionCategory,
    name: 'optionCategory',
    beforeEnter: requireAuth,
  },
  {
    path: '/finrep-product-mapping',
    component: ProductMapping,
    name: 'productMapping',
    beforeEnter: requireAuth,
  },
  {
    path: '/users',
    component: UserManagement,
    name: 'userManagement',
    beforeEnter: requireAuth,
  },
  {
    path: '/tickets',
    component: Tickets,
    name: 'tickets',
    beforeEnter: requireAuth,
  },
  {
    path: '/user_activity_logs',
    component: UserActivityLogs,
    name: 'userActivityLogs',
    beforeEnter: requireAuth,
  },
  {
    path: '/performance_metrics',
    component: PerformanceMetrics,
    name: 'performanceMetrics',
    beforeEnter: requireAuth,
  },
  {
    path: '/admin',
    component: Admin,
    name: 'admin',
    beforeEnter: requireAuth,
  },
  {
    path: '/finrep-konto-mapping',
    component: KontoMapping,
    name: 'kontoMapping',
    beforeEnter: requireAuth,
  },
  {
    path: '/other-finrep',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    // () => import(/* webpackChunkName: "about" */ ${component path})
    component: OtherFinreps,
    name: 'other_finrep',
    beforeEnter: requireAuth,
  },
  {
    path: '/other-type',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    // () => import(/* webpackChunkName: "about" */ ${component path})
    component: OtherFinreps,
    name: 'other_type',
    beforeEnter: requireAuth,
  },
  {
    path: '/validator',
    component: Validator,
    name: 'validator',
    beforeEnter: requireAuth,
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

export default router;
